import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Flame, Snowflake, Zap, ArrowUp, ArrowDown, Star, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SPORT_META } from "@/lib/odds";
import { useAuth } from "@/context/AuthContext";

const KINDS = [["move", "Table moves"], ["power", "Power moves"], ["hot", "Heaters"], ["cold", "Skids"]];
const SCOPES = [["all", "All sports"], ["mine", "My sports"], ["followed", "Followed teams"]];

function TickerPrefs({ prefs, onSaved }) {
  const { setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const save = async (next) => {
    try {
      const { data } = await api.patch("/users/me/preferences", { ticker_prefs: next });
      setUser(data);
      onSaved();
    } catch { toast.error("Could not save ticker preferences"); }
  };
  const toggleKind = (k) => {
    const kinds = prefs.kinds.includes(k) ? prefs.kinds.filter((x) => x !== k) : [...prefs.kinds, k];
    if (!kinds.length) return toast.error("Keep at least one alert type on");
    save({ ...prefs, kinds });
  };
  return {
    button: <button data-testid="ticker-prefs-btn" onClick={() => setOpen(!open)} title="Ticker preferences" className={`rounded-full border p-1.5 transition-colors ${open ? "border-emerald-500 text-emerald-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}><Settings2 size={13} /></button>,
    panel: open && (
      <div data-testid="ticker-prefs-popover" className="fade-up flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-[#26334D] bg-[#0B0E14]/60 px-5 py-3">
        <span className="flex flex-wrap items-center gap-1.5"><span className="label-caps mr-1">Show</span>{KINDS.map(([k, l]) => <button key={k} data-testid={`ticker-kind-${k}`} onClick={() => toggleKind(k)} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${prefs.kinds.includes(k) ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-500 hover:text-white"}`}>{l}</button>)}</span>
        <span className="flex flex-wrap items-center gap-1.5"><span className="label-caps mr-1">Sports</span>{SCOPES.map(([k, l]) => <button key={k} data-testid={`ticker-scope-${k}`} onClick={() => save({ ...prefs, scope: k })} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${prefs.scope === k ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-[#26334D] text-slate-500 hover:text-white"}`}>{l}</button>)}</span>
        <span className="text-[10px] text-slate-500">"My sports" uses your sport preferences · "Followed teams" shows only teams you star</span>
      </div>
    ),
  };
}

const ICON = { move: null, hot: Flame, cold: Snowflake, power: Zap };

export function StandingsTicker() {
  const [d, setD] = useState(null);
  const load = () => api.get("/standings/ticker").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);
  const prefs = d?.prefs || { kinds: ["move", "hot", "cold", "power"], scope: "all" };
  const tp = TickerPrefs({ prefs, onSaved: load });
  if (!d) return null;
  const items = d.items;
  const doubled = [...items, ...items];
  return (
    <section className="glass-card overflow-hidden" data-testid="standings-ticker">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-[#26334D] px-5 py-3">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-300"><Trophy size={14} /> Standings watch</span>
        <span className="text-xs text-slate-400" data-testid="standings-ticker-summary"><span className="font-mono-num text-white">{d.moves}</span> table moves today · <span className="font-mono-num text-orange-300">{d.hot}</span> on heaters · <span className="font-mono-num text-sky-300">{d.cold}</span> in a skid{prefs.scope !== "all" && <span className="ml-2 rounded-full border border-amber-400/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-200" data-testid="ticker-scope-badge">{prefs.scope === "mine" ? "My sports" : "Followed teams"}</span>}</span>
        <span className="ml-auto flex items-center gap-3">
          {tp.button}
          <Link to="/standings" data-testid="standings-ticker-link" className="text-xs font-semibold uppercase tracking-wider text-emerald-400 hover:underline">All standings →</Link>
        </span>
      </div>
      {tp.panel}
      {items.length === 0 && <p className="px-5 py-3 text-xs text-slate-500" data-testid="standings-ticker-empty">Nothing to show with your current ticker filters — {prefs.scope === "followed" ? "follow more teams or widen the sports scope" : "turn on more alert types"}.</p>}
      {items.length > 0 && <div className="overflow-hidden py-2.5">
        <div className="ticker-track flex w-max gap-8" data-testid="standings-ticker-items">
          {doubled.map((x, i) => {
            const Icon = ICON[x.kind];
            return (
              <Link key={`${x.sport}-${x.slug}-${x.kind}-${i}`} to={x.url} data-testid={i < items.length ? "standings-ticker-item" : undefined} className="flex items-center gap-2 whitespace-nowrap text-sm hover:text-emerald-300">
                {x.mine && <Star size={11} className="fill-amber-300 text-amber-300" />}
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: SPORT_META[x.sport]?.color }} />
                <span className="text-slate-500">{x.league}</span>
                <span className="font-semibold text-white">{x.team}</span>
                <span className={`flex items-center gap-1 rounded px-1.5 py-0.5 font-mono-num text-[11px] font-bold ${x.up ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                  {Icon ? <Icon size={10} /> : x.up ? <ArrowUp size={10} /> : <ArrowDown size={10} />}{x.text.replace(/^[▲▼] /, "")}
                </span>
                {x.detail && <span className="text-xs text-slate-500">{x.detail}</span>}
              </Link>
            );
          })}
        </div>
      </div>}
    </section>
  );
}
